import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { CircularProgress, Link, Tooltip, useTheme } from '@material-ui/core'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

import { Page, Header, CustomTable } from 'components'
import { useVestigioService } from 'hooks/useVestigioService'
import { useCestaOperacaoLote } from 'hooks/useCestaOperacaoLote'
import { setIdCaso, setIdVestigio } from 'actions'
import { getStatusCadeiaCustodiaVestigio } from 'helpers/labels'
import useRouter from 'utils/useRouter'
import { FiltrosBancoVestigios } from './components/Filtros'
import useStyles from './components/styles'

export const ListaVestigios = (props) => {
  const { route } = props
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const router = useRouter()
  const vestigioService = useVestigioService()
  const { addVestigio, verificarItemEmCesta, reloadListeners, setReloadListeners } = useCestaOperacaoLote()

  const [vestigios, setVestigios] = useState([])
  const [processandoVestigios, setProcessandoVestigios] = useState(false)

  moment.locale('pt-br', localization)

  useEffect(() => {
    dispatch(setIdCaso(null))
  }, [])

  useEffect(() => {
    vestigioService.filtrarVestigios({}, setVestigios, setProcessandoVestigios)
    if (reloadListeners) {
      setReloadListeners(false)
    }
  }, [reloadListeners])

  const handleRowClick = (rowData) => {
    dispatch(setIdVestigio(rowData.id))
    router.history.push(`/casos/caso/vestigio/detalhar/${rowData.id}`)
  }

  const criarLote = useCallback(async (id) => {
    try {
      setProcessandoVestigios(true)
      const response = await vestigioService.criarLote(id)
      router.history.push(`/vestigios/lote/${response.id}`)
    } catch (error) {
      console.error('Erro ao criar lote:', error)
    } finally {
      setProcessandoVestigios(false)
    }
  }, [])

  const redirectLote = useCallback((id) => {
    router.history.push(`/vestigios/lote/${id}`)
  }, [])

  const colunas = [
    {
      title: 'Identificador',
      field: 'identificador',
      cellStyle: { fontWeight: 600 },
      customSort: (a, b) => a.identificador.localeCompare(b.identificador),
    },
    {
      title: 'Lacre',
      render: (rowData) => rowData.lacre || '-',
      customSort: (a, b) => (a.lacre || '').localeCompare(b.lacre || ''),
    },
    {
      title: 'Lote',
      width: '10%',
      render: (rowData) =>
        rowData.lote ? (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              redirectLote(rowData.lote.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            {rowData.lote.numero}
          </Link>
        ) : (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              criarLote(rowData.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            Criar lote
          </Link>
        ),
      customSort: (a, b) => (a.lote?.numero || '').localeCompare(b.lote?.numero || ''),
    },
    {
      title: 'Descrição',
      field: 'descricao',
      cellStyle: { minWidth: '250px' },
      render: (rowData) => (
        <Tooltip
          title={rowData.descricao?.toUpperCase() || 'SEM DESCRIÇÃO'}
          arrow
          placement="top-start"
          PopperProps={{ popperOptions: { positionFixed: true } }}
        >
          <div
            style={{
              maxWidth: '380px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.palette.text.secondary,
            }}
          >
            {rowData.descricao?.toUpperCase() || 'SEM DESCRIÇÃO REGISTRADA'}
          </div>
        </Tooltip>
      ),
      customSort: (a, b) => (a.descricao || '').localeCompare(b.descricao || ''),
    },
    {
      title: 'Setor atual',
      cellStyle: { minWidth: '350px' },
      render: (rowData) => {
        const textoSetor = rowData.setAtual
          ? `${rowData.setAtual.nome.toUpperCase()} / ${rowData.setAtual.divisao.sigla.toUpperCase()} / ${rowData.setAtual.divisao.regional.sigla.toUpperCase()}`
          : '-'
        return (
          <Tooltip
            title={textoSetor}
            arrow
            placement="top-start"
            PopperProps={{ popperOptions: { positionFixed: true } }}
          >
            <div
              style={{
                maxWidth: '450px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: theme.palette.text.secondary,
              }}
            >
              {textoSetor}
            </div>
          </Tooltip>
        )
      },
      customSort: (a, b) => (a.setAtual?.nome || '').localeCompare(b.setAtual?.nome || ''),
    },
    {
      title: 'Status',
      render: (rowData) => getStatusCadeiaCustodiaVestigio(rowData),
      customSort: (a, b) => getStatusCadeiaCustodiaVestigio(a).localeCompare(getStatusCadeiaCustodiaVestigio(b)),
    },
    {
      title: 'Data',
      render: (rowData) => (rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY HH:mm') : '-'),
      customSort: (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
    },
  ]

  const acoesCesta = [
    (rowData) => ({
      icon: () => (
        <img
          src="/images/cesta-add-black.png"
          alt="Adicionar à cesta"
          className="img-icon-invert-dark"
          style={{
            width: 24,
            height: 24,
          }}
        />
      ),
      tooltip: 'Adicionar à cesta',
      onClick: (event, rowData) => addVestigio(rowData),
      hidden: verificarItemEmCesta(rowData.id),
    }),
  ]

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <FiltrosBancoVestigios setVestigios={setVestigios} setProcessandoVestigios={setProcessandoVestigios} />

      {processandoVestigios ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <CustomTable
          columns={colunas}
          data={vestigios}
          actions={acoesCesta}
          onRowClick={(event, rowData) => handleRowClick(rowData)}
          searchPlaceholder="Buscar vestígios..."
          emptyDataSourceMessage="Nenhum vestígio encontrado"
        />
      )}
    </Page>
  )
}
